// src/contexts/pessoas/FornecedorContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { fetchData, postData, putData, deleteData } from '../../services/Api';
import { useAlert } from '../geral/AlertContext';
import FornecedorSearch from '../../pages/pessoas/FornecedorSearch';
import FornecedorCad from '../../pages/pessoas/FornecedorCad';
import { Fornecedor } from '../../models/pessoas/Fornecedor.js';

const FornecedorContext = createContext();

let fornecedorCad;

export const useFornecedor = () => {
    const context = useContext(FornecedorContext);
    if (!context) {
        throw new Error('useFornecedor must be used within a FornecedorProvider');
    }
    return context;
};

export const FornecedorProvider = ({ children }) => {
    const [fornecedores, setFornecedores] = useState([]);
    const [fornecedorSel, setFornecedorSel] = useState(new Fornecedor());
    const { showAlert } = useAlert();
    const [showSearchModalFornecedor, setShowSearchModalFornecedor] = useState(false);    
    const tempOnSelectFornecedorRef = useRef(null);

    const showFornecedorSearch = () => {
        return new Promise((resolve) => {
            setShowSearchModalFornecedor(true);
            const handleSelect = (fornecedorSelect) => {
                setShowSearchModalFornecedor(false);
                resolve(fornecedorSelect || null);
            };
            tempOnSelectFornecedorRef.current = handleSelect;
        });
    };

    const handleCloseSearchModal = () => {
        setShowSearchModalFornecedor(false);
        tempOnSelectFornecedorRef.current?.(null);
        tempOnSelectFornecedorRef.current = null;
    };

    const [showModalFornecedor, setShowModalFornecedor] = useState(false);
    const tempOnSaveFornecedorRef = useRef(null);
    const showFornecedorCad = (fornecedor) => {
        if (fornecedor?.pessoa) {
            fornecedorCad = fornecedor;
        } else {
            fornecedorCad = undefined;
        }
        setShowModalFornecedor(true);
        return new Promise((resolve) => {
            tempOnSaveFornecedorRef.current = resolve;
        });
    };

    const handleCloseModalFornecedor = () => {
        setShowModalFornecedor(false);
        tempOnSaveFornecedorRef.current?.(null);
        tempOnSaveFornecedorRef.current = null;
        fornecedorCad = undefined;
    };

    const handleSaveModalFornecedor = (fornecedorSalvo) => {
        setShowModalFornecedor(false);
        tempOnSaveFornecedorRef.current?.(fornecedorSalvo || null);
        tempOnSaveFornecedorRef.current = null;
        fornecedorCad = undefined;
    };

    useEffect(() => {
        const fetchDataFromAPI = async () => {
            try {
                const result = await fetchData('/Fornecedor');
                setFornecedores(result);
            } catch (error) {
                console.error("Erro ao buscar fornecedores:", error);
            }
        };
        fetchDataFromAPI();
    }, []);

    const addFornecedor = async (fornecedor) => {
        try {
            const result = await postData('/Fornecedor', fornecedor);
            setFornecedores((atuais) => [...atuais, result]);
            setFornecedorSel(result);
            showAlert('Fornecedor adicionado com sucesso!');
            return result;
        } catch (error) {
            console.error("Erro ao adicionar fornecedor:", error);
            showAlert('Erro ao adicionar fornecedor!', 'danger');
            throw error;
        }
    };

    const updateFornecedor = async (fornecedor) => {
        try {
            const result = await putData('/Fornecedor', fornecedor);
            setFornecedores((atuais) => atuais.map(f => f.id === fornecedor.id ? result : f));
            setFornecedorSel(result);
            showAlert('Fornecedor atualizado com sucesso!');
            return result;
        } catch (error) {
            console.error("Erro ao atualizar fornecedor:", error);
            showAlert('Erro ao atualizar fornecedor!', 'danger');
            throw error;
        }
    };

    const deleteFornecedor = async (id) => {
        try {
            await deleteData('/Fornecedor', { fornecedorId: id });
            setFornecedores((atuais) => atuais.filter(f => f.id !== id));
            showAlert('Fornecedor excluído com sucesso!');
        } catch (error) {
            console.error("Erro ao excluir fornecedor:", error);
            showAlert('Erro ao excluir fornecedor!', 'danger');
            throw error.data || error;
        }
    };

    const handleSelectFornecedor = () => {
        setShowSearchModalFornecedor(false);
    };

    const handleSelectFornecedorSearch = (fornecedorSelect) => {
        const onSelect = tempOnSelectFornecedorRef.current || handleSelectFornecedor;
        onSelect(fornecedorSelect);
    };

    return (
        <FornecedorContext.Provider value={{ fornecedorSel, setFornecedorSel, fornecedores, addFornecedor, updateFornecedor, deleteFornecedor, showFornecedorSearch, showFornecedorCad }}>
            {children}
            <FornecedorSearch
                show={showSearchModalFornecedor}
                onHide={handleCloseSearchModal}
                onSelectFornecedor={handleSelectFornecedorSearch}
            />
            <FornecedorCad
                show={showModalFornecedor}
                onHide={handleCloseModalFornecedor}                
                initialFornecedor={fornecedorCad}
                onSaveFornecedor={handleSaveModalFornecedor}
            />
        </FornecedorContext.Provider>
    );
};
